import { Notify, copyToClipboard } from "quasar";
import { getFriendlyErrorMessage } from "src/utils/facilityErrors";

// --- Component toast suppression ---
let suppressUntil = 0;

function isErrorToast(opts) {
  return (
    opts.color === "red" ||
    opts.color === "negative" ||
    opts.type === "negative"
  );
}

export function installNotifySuppression(app) {
  const $q = app.config.globalProperties.$q;
  const original = $q.notify;
  $q.notify = function (opts) {
    if (
      typeof opts === "object" &&
      isErrorToast(opts) &&
      Date.now() < suppressUntil
    ) {
      return () => {}; // no-op dismiss
    }
    return original.call(this, opts);
  };
}

// --- Toast deduplication & capping ---
const activeToasts = new Map(); // key → { dismiss, timestamp }
const MAX_TOASTS = 3;
const DEDUP_WINDOW_MS = 1000;

function toastKey(detail) {
  return `${detail.method || ""}:${detail.url || ""}:${detail.status || ""}`;
}

function dismissOldest() {
  let oldestKey = null;
  let oldestTime = Infinity;
  for (const [key, entry] of activeToasts) {
    if (entry.timestamp < oldestTime) {
      oldestTime = entry.timestamp;
      oldestKey = key;
    }
  }
  if (oldestKey) {
    activeToasts.get(oldestKey)?.dismiss();
    activeToasts.delete(oldestKey);
  }
}

// --- Request body formatting ---
function formatRequestBody(raw) {
  if (!raw) return null;
  if (typeof raw === "object" && typeof raw.append === "function")
    return "FormData";
  if (raw instanceof Blob) return "Binary data";

  let text;
  if (typeof raw === "string") {
    text = raw;
  } else {
    try {
      text = JSON.stringify(raw);
    } catch {
      return "Unserializable data";
    }
  }

  if (text.length > 2000) {
    text = text.slice(0, 2000) + "...(truncated)";
  }
  return text;
}

// --- Clipboard text builder ---
export function buildClipboardText(detail) {
  const lines = [];
  lines.push(`Timestamp  : ${detail.timestamp || new Date().toISOString()}`);
  lines.push(`Endpoint   : ${detail.method || "?"} ${detail.url || "unknown"}`);
  lines.push(`Status     : ${detail.status || "N/A"}`);
  lines.push(`Message    : ${detail.message || ""}`);

  if (detail.requestId) {
    lines.push(`Request ID : ${detail.requestId}`);
  }

  const body = formatRequestBody(detail.requestBody);
  if (body) {
    lines.push(`Request body:`);
    lines.push(`  ${body}`);
  }

  if (detail.errors && typeof detail.errors === "object") {
    lines.push(`Field errors:`);
    for (const [field, msgs] of Object.entries(detail.errors)) {
      const messages = Array.isArray(msgs) ? msgs : [msgs];
      for (const m of messages) {
        lines.push(`  ${field}: ${m}`);
      }
    }
  }

  return lines.join("\n");
}

// --- Toast message builders ---
function buildValidationMessage(detail) {
  const errors = detail.errors || {};
  const fieldCount = Object.keys(errors).length;
  let message = detail.message;
  if (typeof detail.errors !== 'undefined') {
    message = `Validation failed: ${fieldCount} field error(s)`;
  }

  const captionLines = [];
  let count = 0;
  for (const [field, msgs] of Object.entries(errors)) {
    const messages = Array.isArray(msgs) ? msgs : [msgs];
    for (const m of messages) {
      if (count >= 5) break;
      captionLines.push(`${field}: ${m}`);
      count++;
    }
    if (count >= 5) break;
  }

  return { message, caption: captionLines.join("\n") };
}

function buildServerErrorMessage(detail) {
  return {
    message: detail.message || `Server error (${detail.status})`,
    caption: "",
  };
}

function buildNetworkErrorMessage() {
  return {
    message: "Network error — could not reach the server",
    caption: "",
  };
}

// --- Extract a user-presentable error message from an Axios error ---
function extractApiErrorMessage(error) {
  if (!error.response) {
    return error.message || "No response from server";
  }
  const { status, data } = error.response;
  if (data && typeof data === "object" && data.message) {
    return (
      getFriendlyErrorMessage(data.message) ||
      data.message ||
      `Request failed (${status})`
    );
  }
  return `Request failed (${status})`;
}

// --- Build detail object from raw Axios error and show toast ---
export function handleApiError(error) {
  const status = error.response?.status || null;
  const data = error.response?.data;
  const detail = {
    status,
    url: error.config?.url,
    method: error.config?.method?.toUpperCase(),
    message: extractApiErrorMessage(error),
    errors: status === 422 && typeof data === "object" ? data?.errors : null,
    requestBody: error.config?.data,
    requestId:
      error.config?.requestId ||
      error.response?.headers?.["x-request-id"] ||
      error.response?.headers?.["x-trace-id"] ||
      null,
    timestamp: new Date().toISOString(),
  };
  showApiErrorNotify(detail);
}

// --- Main toast function ---
export function showApiErrorNotify(detail) {
  const key = toastKey(detail);
  const now = Date.now();

  // Deduplicate: skip if same key within dedup window
  const existing = activeToasts.get(key);
  if (existing && now - existing.timestamp < DEDUP_WINDOW_MS) {
    return;
  }

  // If same key exists but dedup window expired, dismiss the stale toast
  if (existing) {
    existing.dismiss();
    activeToasts.delete(key);
  }

  // Cap: dismiss oldest if at max
  if (activeToasts.size >= MAX_TOASTS) {
    dismissOldest();
  }

  // Build message based on error type
  let toastContent;
  if (!detail.status) {
    toastContent = buildNetworkErrorMessage();
  } else if (detail.status === 422) {
    toastContent = buildValidationMessage(detail);
  } else {
    toastContent = buildServerErrorMessage(detail);
  }

  // Suppress component-level error toasts for 500ms
  suppressUntil = Date.now() + 500;

  const onDismiss = () => {
    activeToasts.delete(key);
  };

  // Use Notify.create so global toasts bypass the suppression wrapper
  const dismiss = Notify.create({
    type: "negative",
    position: "top",
    timeout: 20000,
    multiLine: false,
    message: toastContent.message,
    caption: toastContent.caption || undefined,
    style: "white-space: pre-line",
    onDismiss,
    actions: [
      {
        label: "Copy for Support",
        icon: "content_copy",
        color: "white",
        flat: true,
        handler: () => {
          const text = buildClipboardText(detail);
          copyToClipboard(text)
            .then(() => {
              Notify.create({
                message: "Copied!",
                color: "positive",
                timeout: 2000,
                position: "top",
              });
            })
            .catch(() => {
              Notify.create({
                message: "Could not copy to clipboard",
                color: "warning",
                timeout: 3000,
                position: "top",
              });
            });
        },
      },
      {
        icon: "close",
        color: "white",
        round: true,
        handler: () => {
          /* dismiss handled by onDismiss */
        },
      },
    ],
  });

  activeToasts.set(key, { dismiss, timestamp: now });
}
